import React, { useCallback } from 'react';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import style from './ddp-terms-and-conditions-modal.module.less';
import { DDP_MODAL_SNOOZE_KEY, BASE_TERMS_URL } from '../../../config/ddp-country-config';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { useDdpCountryConfig } from '../../../hooks/use-ddp-country-config';
import { useLocalStorage } from '../../../hooks/use-local-storage';
import { updateSellerSettingsBulk } from '../../../pages/settings/api/save-settings';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { SimpleModal } from '../../simple-modal';
import { acceptNewTermsAndConditions } from '../new-terms-modal/api';

/**
 * Modal for DDP Terms and Conditions acceptance
 * Uses country-specific configuration for terms label, URL, and messages
 * Only opened when DDP is available for seller's country
 * @param {object} props
 * @param {() => void} props.onClose - Callback when modal is closed
 * @returns {React.JSX.Element}
 */
export const DdpTermsAndConditionsModal = ({ onClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [checkedTerms, setCheckedTerms] = useAsyncSafeState(false);
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [, setSnoozedUntilTimestamp] = useLocalStorage(DDP_MODAL_SNOOZE_KEY);

  // Get country-specific configuration
  const { termsLabel, termsMessages: messages, snoozeDurationHours } = useDdpCountryConfig();

  const handleRemindMeLater = useCallback(() => {
    // Just close the modal - no snooze needed
    // The modal will reappear if user tries to create DDP Walleted International again
    onClose();
  }, [onClose]);

  const handleAccept = useCallback(async () => {
    if (!checkedTerms || !termsLabel) return;

    try {
      setIsSaving(true);
      // Use country-specific terms label for API call
      await acceptNewTermsAndConditions(termsLabel);

      const newSettings = await updateSellerSettingsBulk({
        [SellerSettings.billDutiesAndTaxesToPayor.key]: true,
      });
      dispatch(saveNewSellerSettings(newSettings));

      const snoozeUntilTimestamp = `"${DateTime.now().plus({ hours: snoozeDurationHours }).toISO()}"`;
      setSnoozedUntilTimestamp(snoozeUntilTimestamp);

      onClose();
    } catch {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.errorTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: <FormattedMessage {...messages.acceptError} />,
      });
    } finally {
      setIsSaving(false);
    }
  }, [checkedTerms, termsLabel, dispatch, onClose, setIsSaving, setSnoozedUntilTimestamp, snoozeDurationHours, messages]);

  return (
    <SimpleModal
      actionMessage={messages.acceptButton}
      bodyClassName={style['modal-body']}
      cancelMessage={messages.cancelButton}
      className={style['ddp-terms-modal']}
      headerMessage={messages.modalTitle}
      isActionDisabled={!checkedTerms || isSaving}
      isActionLoading={isSaving}
      onAction={handleAccept}
      onCancel={onClose}
      onClose={onClose}
      onSecondAction={handleRemindMeLater}
      secondActionMessage={messages.remindMeLaterButton}
      size='custom'
    >
      <FlexColumn className={style['modal-content']}>
        <Text className={style['main-text']} textType={TextType.Paragraph} value={messages.mainTextParagraph1} />
        <Text className={style['main-text']} textType={TextType.Paragraph} value={messages.mainTextParagraph2} />

        <FlexColumn className={style['checkbox-section']}>
          <Checkbox
            checked={checkedTerms}
            className={style['terms-checkbox']}
            disabled={isSaving}
            label={intl.formatMessage(messages.checkboxLabel)}
            largeCheckbox
            onChange={setCheckedTerms}
          />
        </FlexColumn>

        <FlexColumn className={style['link-section']}>
          <Button className={style['terms-link']} href={BASE_TERMS_URL} theme={THEME_CONSTANTS.tertiary}>
            <FlexRow>
              <Text className={style['terms-link-text']} textType={TextType.Caption} value={messages.termsLinkText} />
              <FontAwesomeIcon className={style['external-link-icon']} icon={faArrowUpRightFromSquare} />
            </FlexRow>
          </Button>
        </FlexColumn>
      </FlexColumn>
    </SimpleModal>
  );
};
